/**
 * Piezas chicas que comparten las pantallas de la base de datos (cargas, contactos y públicos).
 *
 * - `llamar` junta en UNA forma lo que `req()` devuelve (2xx o 422 sin lanzar) y lo que lanza (409,
 *   404, 413, 5xx, red). Las pantallas de la base reaccionan a códigos que llegan por los dos caminos
 *   (un 409 `CARGA_DESACTUALIZADA` se lanza; un 422 `MAPEO_COLUMNAS_INVALIDO` se devuelve), y leer
 *   uno solo es la forma de que un rechazo termine pintado como éxito.
 * - Los estados de carga, error y vacío tienen el mismo aspecto que el resto del oficial.
 */

import type { ReactNode } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import type { EstadoCarga, PaginaErroresCarga } from "../../types.base";
import { baseApi } from "../../baseApi";
import { erroresACsv } from "../../logic/base";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import type { Lectura } from "../../logic/respuesta";

export const R_BASE = "/activa/oficial/base";
/**
 * Las cargas con consentimiento del oficial viven en Público desde el 17/10/2026 (la Base salió del menú, pedido de
 * Alan): la lista es la pestaña «Con consentimiento» y el asistente y el detalle, `R_CARGAS/nueva` y `R_CARGAS/:id`.
 */
export const R_CARGAS = "/activa/publico/cargas";
export const R_CARGAS_LISTA = "/activa/publico?vista=consentimientos";

export type Fallo = Extract<Lectura<unknown>, { ok: false }>;

/** Ejecuta un pedido y devuelve siempre una `Lectura`: nunca lanza. */
export async function llamar<D>(pedido: () => Promise<unknown>): Promise<Lectura<D>> {
  try {
    return leerRespuesta<D>(await pedido());
  } catch (e) {
    const x = leerError(e);
    const extra: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(x.body)) if (!["status", "success", "error", "code", "data"].includes(k)) extra[k] = v;
    return { ok: false, status: x.status, code: x.code, error: x.error, extra };
  }
}

/** Texto para el operador de un rechazo: el del backend o el genérico por status. Un 413 dice el tope del archivo. */
export function textoFallo(f: Pick<Fallo, "status" | "error" | "code">): string {
  if (f.status === 413 || f.code === "DEMASIADO_GRANDE") return "El archivo es demasiado grande (máximo 8 MB de datos).";
  return textoError(f);
}

/** Los `errores` que el backend manda en el primer nivel (MAPEO_COLUMNAS_INVALIDO): sólo strings. */
export function erroresDe(f: Fallo): string[] {
  const e = f.extra?.errores;
  return Array.isArray(e) ? e.filter((x): x is string => typeof x === "string" && !!x.trim()) : [];
}

/** «hace 5 min», «hace 3 h», «hace 2 días». Una fecha que no se entiende no dice nada. */
export function haceCuanto(iso: string | null | undefined, ahora: Date = new Date()): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.round((ahora.getTime() - t) / 60_000));
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? "hace 1 día" : `hace ${d} días`;
}

/** Cómo dieron el consentimiento: el `select` del paso 3 de la carga y del registro manual en la ficha. */
export const FUENTES_CONSENTIMIENTO = [
  "Formulario web", "Formulario en papel", "Evento o feria", "Compra en el local", "Programa de puntos",
] as const;
export const FUENTE_OTRA = "Otra";

/** La fuente que se manda: la opción elegida, o el texto escrito si eligió «Otra». */
export function fuenteElegida(opcion: string, otra: string): string {
  return opcion === FUENTE_OTRA ? otra.trim().slice(0, 100) : opcion;
}
/** Al revés, para retomar lo guardado: una fuente que no es una de las opciones va como «Otra» con su texto. */
export function separarFuente(fuente: string): { opcion: string; otra: string } {
  if (!fuente) return { opcion: "", otra: "" };
  return (FUENTES_CONSENTIMIENTO as readonly string[]).includes(fuente) ? { opcion: fuente, otra: "" } : { opcion: FUENTE_OTRA, otra: fuente };
}

export const FUENTE_BASE: Record<string, string> = {
  archivo_columna: "La columna del archivo",
  archivo_declaracion: "La declaración del archivo",
  manual: "Cambio manual",
  whatsapp_qr: "Escribió por el QR de WhatsApp",
};

/* ── Estados de una carga (SPEC-BD §6.2) ─────────────────────────────────────────────────── */

export function ChipEstadoCarga({ estado, progreso }: { estado: EstadoCarga; progreso?: { procesadas: number; total: number } | null }) {
  const base = "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1";
  switch (estado) {
    case "borrador": return <span className={`${base} bg-slate-100 text-slate-600`}>Sin importar</span>;
    case "importando": {
      const pct = progreso && progreso.total > 0 ? Math.min(100, Math.floor((progreso.procesadas / progreso.total) * 100)) : 0;
      return <span className={`${base} bg-sky-50 text-sky-700`}><Loader2 size={10} className="animate-spin" /> Importando {pct}%</span>;
    }
    case "importada": return <span className={`${base} bg-emerald-50 text-emerald-700`}>Importada</span>;
    case "fallida": return <span className={`${base} bg-red-50 text-red-700`}>No terminó</span>;
    case "eliminando": return <span className={`${base} bg-amber-50 text-amber-700`}>Borrando…</span>;
    case "eliminada": return <span className={`${base} bg-slate-100 text-slate-500 line-through`}>Datos borrados</span>;
    default: return <span className={`${base} bg-slate-100 text-slate-500`}>{String(estado || "sin estado")}</span>;
  }
}

/* ── Estados de pantalla ─────────────────────────────────────────────────────────────────── */

export function CajaError({ titulo, detalle, onReintentar, children }: { titulo: string; detalle?: string; onReintentar?: () => void; children?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700" role="alert">
      <p className="font-semibold">{titulo}</p>
      {detalle && <p className="mt-1">{detalle}</p>}
      {children}
      {onReintentar && (
        <button type="button" onClick={onReintentar} className="mt-3 inline-flex items-center gap-1.5 font-semibold underline">
          <RefreshCw size={13} /> Reintentar
        </button>
      )}
    </div>
  );
}

export function Esqueleto({ filas = 3, alto = "h-14" }: { filas?: number; alto?: string }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: filas }, (_, i) => <div key={i} className={`${alto} rounded-2xl bg-slate-100 animate-pulse`} />)}
    </div>
  );
}

export function AvisoAmbar({ children }: { children: ReactNode }) {
  return <div className="rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">{children}</div>;
}

/** Descarga un texto como archivo (el detalle de errores de una carga). */
export function descargarTexto(contenido: string, nombre: string, tipo = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Se libera en la vuelta siguiente: algunos navegadores cancelan la descarga si el URL muere en el mismo tick.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** El nombre del CSV de errores: el de la carga, sin caracteres que un sistema de archivos rechace. */
export function nombreCsvErrores(nombreCarga: string): string {
  const limpio = String(nombreCarga || "carga").replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "carga";
  return `errores - ${limpio}.csv`;
}

/**
 * «Descargar el detalle (CSV)»: los primeros 5.000 errores de la carga (los que guarda el backend), con
 * los textos de la pantalla. Devuelve el texto del error si no se pudo, o "" si se descargó.
 */
export async function descargarErroresCarga(id: string, nombreCarga: string): Promise<string> {
  const l = await llamar<PaginaErroresCarga>(() => baseApi.erroresCarga(id, { limit: 5000 }));
  if (!l.ok) return textoFallo(l);
  descargarTexto(erroresACsv(Array.isArray(l.data?.items) ? l.data.items : []), nombreCsvErrores(nombreCarga));
  return "";
}
